import { useState } from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Gift, CheckCircle, XCircle, ArrowRight } from 'lucide-react';

export default function BonCadeauPage() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const res = await api.get(`/gift-cards/validate/${code.trim().toUpperCase()}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Code invalide ou déjà utilisé');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <SEO
        title="Bon Cadeau — Offrez un Moment de Beauté"
        description="Offrez un bon cadeau Villa Marceau et faites plaisir à vos proches avec des pièces uniques et des objets de décoration artisanaux."
        canonical="/bon-cadeau"
      />
      <div className="py-20 px-6 lg:px-12">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="w-10 h-10 text-primary" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-3">Bon cadeau</h1>
            <p className="text-muted-foreground">
              Vérifiez le solde de votre bon cadeau Villa Marceau ou utilisez-le lors de votre commande en ligne.
            </p>
          </div>

          {/* Check form */}
          <form onSubmit={handleCheck} className="flex gap-3 mb-6">
            <Input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="VM-XXXX-XXXX"
              className="font-mono text-center tracking-widest text-lg flex-1"
              maxLength={12}
              data-testid="gift-card-code-input"
            />
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="check-gift-card-btn">
              {loading ? 'Vérification...' : 'Vérifier'}
            </Button>
          </form>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-testid="gift-card-error">
              <XCircle className="w-5 h-5 text-red-500 shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Success result */}
          {result && (
            <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6" data-testid="gift-card-result">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <p className="font-semibold text-green-800">Bon cadeau valide</p>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Code</span>
                  <span className="font-mono font-bold">{result.code}</span>
                </div>
                {result.recipient_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Destinataire</span>
                    <span>{result.recipient_name}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valeur initiale</span>
                  <span>{result.initial_amount?.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between font-semibold text-base border-t border-stone-200 pt-2 mt-2">
                  <span>Solde disponible</span>
                  <span className="text-primary text-xl">{result.remaining_amount?.toFixed(2)} €</span>
                </div>
              </div>

              {result.message && (
                <div className="bg-white border border-stone-100 rounded-xl p-4 mb-4 italic text-sm text-muted-foreground">
                  "{result.message}"
                </div>
              )}

              <Link to="/commander-en-ligne">
                <Button className="btn-primary w-full">
                  Utiliser sur la boutique
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {/* In-store info */}
          <div className="mt-8 p-4 bg-muted/50 rounded-xl border border-border text-center text-sm text-muted-foreground">
            <p className="font-medium mb-1">Utilisation en boutique</p>
            <p>Présentez votre code à la boutique au <strong>26 rue Marceau, 37000 Tours</strong>.</p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
